/**
 * PaymentContext - Contexto de Pagamento
 * 
 * Responsabilidades:
 * - Gerenciar método de pagamento selecionado
 * - Gerenciar dados do cartão
 * - Gerenciar parcelamento
 * - Persistir no localStorage (criptografado)
 * - Otimizado com useMemo e useCallback
 */

import React, { createContext, useState, useEffect, useMemo, useCallback, ReactNode, useContext } from 'react';
 
import { saveEncrypted, loadEncrypted } from '../../utils/encryptedStorage';
import useAuth from '../../hooks/useAuth';
import { PaymentContextType, PaymentProviderProps, PaymentState } from './paymentContext.type';
import { PaymentMethod, Card, Installment } from '../orderMetaDataContext/orderMetaData.type';

const STORAGE_KEY = 'checkout_payment';
const PLUGINSPACE_STORAGE_KEY = 'pluginspace_context_guard';
const PLUGINSPACE_CHANGE_EVENT = 'pluginspace:change';

const DEFAULT_STATE: PaymentState = {
  paymentMethod: null,
  card: null,
  selectedInstallment: null,
  availableInstallments: [],
  gatewayId: 0,
  changePayment: 0,
  isAfter: false,
  method: 'delivery-payment',
};

// ============ CONTEXT ============

export const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

// ============ PROVIDER ============

export function PaymentProvider({ children }: PaymentProviderProps) {
  const { currentPluginspaceId } = useAuth();
  const [state, setState] = useState<PaymentState>(() => {
    // Carregar do localStorage na inicialização
    const saved = loadEncrypted<PaymentState>(STORAGE_KEY);
    return saved || DEFAULT_STATE;
  });

  useEffect(() => {
    if (!currentPluginspaceId) return;
    const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
    const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
    const currentId = String(currentPluginspaceId);

    if (storedPluginspaceId && storedPluginspaceId !== currentId) {
      setState(DEFAULT_STATE);
    }

    if (storedPluginspaceId !== currentId) {
      saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: currentPluginspaceId });
    }
  }, [currentPluginspaceId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePluginspaceChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ pluginspaceId: number }>;
      const newPluginspaceId = customEvent.detail?.pluginspaceId;
      if (!newPluginspaceId) return;

      const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
      const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
      const nextId = String(newPluginspaceId);

      if (storedPluginspaceId && storedPluginspaceId !== nextId) {
        setState(DEFAULT_STATE);
      }

      if (storedPluginspaceId !== nextId) {
        saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: newPluginspaceId });
      }
    };

    window.addEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
    return () => window.removeEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
  }, []);

  // ============ PERSISTÊNCIA ============
  
  useEffect(() => {
    // Salvar no localStorage sempre que o estado mudar
    // IMPORTANTE: Card contém dados sensíveis (criptografado)
    saveEncrypted(STORAGE_KEY, state);
  }, [state]);

  // ============ AÇÕES - PAYMENT METHOD (MEMOIZADAS) ============

  const setPaymentMethod = useCallback((method: PaymentMethod) => {
    setState(prev => ({ ...prev, paymentMethod: method }));
  }, []);

  const setGateWayId = useCallback((gatewayId: number) => {
    setState(prev => ({ ...prev, gatewayId }));
  }, []);

  const setPaymentIsAfter = useCallback((isAfter: boolean) => {
    setState(prev => ({ ...prev, isAfter }));
  }, []);

  const clearPaymentMethod = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      paymentMethod: null,
      card: null,
      selectedInstallment: null,
      availableInstallments: [],
    }));
  }, []);

  // ============ AÇÕES - CARD (MEMOIZADAS) ============

  const setCard = useCallback((card: Card) => {
    setState(prev => ({ ...prev, card }));
  }, []);

  const updateCard = useCallback((updates: Partial<Card>) => {
    setState(prev => ({
      ...prev,
      card: prev.card ? { ...prev.card, ...updates } : null,
    }));
  }, []);

  const clearCard = useCallback(() => {
    setState(prev => ({ ...prev, card: null }));
  }, []);

  // ============ AÇÕES - INSTALLMENTS (MEMOIZADAS) ============

  const setSelectedInstallment = useCallback((installment: Installment) => {
    setState(prev => ({ ...prev, selectedInstallment: installment }));
  }, []);

  const setAvailableInstallments = useCallback((installments: Installment[]) => {
    setState(prev => ({ ...prev, availableInstallments: installments }));
  }, []);

  const setChangePayment = useCallback((changePayment: number) => {
    setState(prev => ({ ...prev, changePayment: changePayment }));
  }, []);

  const calculateInstallments = useCallback((total: number, maxInstallments: number, hasInterest: boolean): Installment[] => {
    const installments: Installment[] = [];
    
    for (let i = 1; i <= maxInstallments; i++) {
      const interestRate = hasInterest ? 0.0199 : 0; // 1.99% ao mês
      const installmentValue = hasInterest 
        ? (total * Math.pow(1 + interestRate, i)) / i
        : total / i;
      
      installments.push({
        number: i,
        value: installmentValue,
        total: installmentValue * i,
        hasInterest,
      });
    }
    
    setState(prev => ({ ...prev, availableInstallments: installments }));
    return installments;
  }, []);

  const clearInstallments = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      selectedInstallment: null,
      availableInstallments: [],
    }));
  }, []);

  // ============ QUERIES (MEMOIZADAS) ============

  const requiresCard = useCallback(() => {
    const method = state.paymentMethod?.typePayment;
    return method === 'cielo-card' || method === 'pagseguro-card' || method === 'esitef-card' || method === 'totvs-card' || method === 'rede-card';
  }, [state.paymentMethod]);

  const isPaymentComplete = useCallback(() => {
    if (!state.paymentMethod) return false;
    
    // Se requer cartão, validar dados do cartão
    if (requiresCard()) {
      if (!state.card) return false;
      const { number, holderName, expiryDate, cvv } = state.card;
      if (!number || !holderName || !expiryDate || !cvv) return false;
    }
    
    // Se é cartão de crédito com parcelamento, validar seleção
    if (state.paymentMethod.typePayment === 'cielo-card' || state.paymentMethod.typePayment === 'pagseguro-card' || state.paymentMethod.typePayment === 'esitef-card' || state.paymentMethod.typePayment === 'totvs-card' || state.paymentMethod.typePayment === 'rede-card' && state.paymentMethod.installments) {
      if (!state.selectedInstallment) return false;
    }
    
    return true;
  }, [state.paymentMethod, state.card, state.selectedInstallment, requiresCard]);

  const getPaymentTotal = useCallback(() => {
    if (state.selectedInstallment) {
      return state.selectedInstallment.total;
    }
    return 0;
  }, [state.selectedInstallment]);

  // ============ VALUE (MEMOIZADO) ============

  const value = useMemo<PaymentContextType>(() => ({
    // Estado
    paymentMethod: state.paymentMethod,
    card: state.card,
    selectedInstallment: state.selectedInstallment,
    availableInstallments: state.availableInstallments,
    gatewayId: state.gatewayId,
    changePayment: state.changePayment,
    
    // Ações - Payment Method
    setPaymentMethod,
    setPaymentIsAfter,
    clearPaymentMethod,
    setGateWayId,
    
    // Ações - Card
    setCard,
    updateCard,
    clearCard,
    
    // Ações - Installments
    setSelectedInstallment,
    setAvailableInstallments,
    calculateInstallments,
    clearInstallments,
    setChangePayment,
    
    // Queries
    isPaymentComplete,
    requiresCard,
    getPaymentTotal,
  }), [
    state.paymentMethod,
    state.card,
    state.gatewayId,
    state.selectedInstallment,
    state.availableInstallments,
    state.changePayment,
    setPaymentMethod,
    setPaymentIsAfter,
    clearPaymentMethod,
    setCard,
    updateCard,
    clearCard,
    setSelectedInstallment,
    setAvailableInstallments,
    calculateInstallments,
    clearInstallments,
    isPaymentComplete,
    requiresCard,
    getPaymentTotal,
    setChangePayment,
    setGateWayId,
  ]);

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
}

export function usePayment() {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within PaymentProvider');
  }
  return context;
}

/**
 * Hook seletor - apenas método de pagamento
 */
export function usePaymentMethod() {
  const { paymentMethod } = usePayment();
  return useMemo(() => paymentMethod, [paymentMethod]);
}

/**
 * Hook seletor - apenas dados do cartão
 */
export function usePaymentCard() {
  const { card } = usePayment();
  return useMemo(() => card, [card]);
}

/**
 * Hook seletor - apenas parcela selecionada
 */
export function usePaymentInstallment() {
  const { selectedInstallment } = usePayment();
  return useMemo(() => selectedInstallment, [selectedInstallment]);
}

/**
 * Hook seletor - apenas parcelas disponíveis
 */
export function usePaymentInstallments() {
  const { availableInstallments } = usePayment();
  return useMemo(() => availableInstallments, [availableInstallments]);
}

/**
 * Hook seletor - apenas ações
 * NUNCA re-renderiza
 */
export function usePaymentActions() {
  const {
    setPaymentMethod,
    clearPaymentMethod,
    setCard,
    updateCard,
    clearCard,
    setSelectedInstallment,
    setAvailableInstallments,
    calculateInstallments,
    clearInstallments,
  } = usePayment();

  return useMemo(() => ({
    setPaymentMethod,
    clearPaymentMethod,
    setCard,
    updateCard,
    clearCard,
    setSelectedInstallment,
    setAvailableInstallments,
    calculateInstallments,
    clearInstallments,
  }), [
    setPaymentMethod,
    clearPaymentMethod,
    setCard,
    updateCard,
    clearCard,
    setSelectedInstallment,
    setAvailableInstallments,
    calculateInstallments,
    clearInstallments,
  ]);
}

/**
 * Hook seletor - validações
 */
export function usePaymentValidation() {
  const { isPaymentComplete, requiresCard } = usePayment();
  
  return useMemo(() => ({
    isPaymentComplete: isPaymentComplete(),
    requiresCard: requiresCard(),
  }), [isPaymentComplete, requiresCard]);
}

/**
 * Hook seletor - total do pagamento
 */
export function usePaymentTotal() {
  const { getPaymentTotal } = usePayment();
  return useMemo(() => getPaymentTotal(), [getPaymentTotal]);
}
